import { Check, UserAdd, X } from 'reicon-react';
import { useState, type ReactElement } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useFocoDeModal } from '@/lib/useFocoDeModal';
import { DURACAO_SAIDA_MODAL_MS, usePresenca } from '@/lib/usePresenca';
import { formatarCEP, validarFormatoCEP, validarFormatoCPF } from '../../domain/cliente/documento';
import type { CadastroSimplificadoInput } from '../../domain/cliente/clienteVenda';

/**
 * Cadastro simplificado de cliente (T024, `CLI-03`/`CLI-04`) — réplica do frame
 * "PDV Online Web - Modal cadastro de cliente" do Pencil
 * (`design/CentriumCheckout.pen`, nó `R6FmKw`/`m5As4q`, lido via MCP): modal de
 * 640px, cabeçalho de 78px com ícone `user-round-plus`, conteúdo em duas seções
 * ("Dados do cliente" e "Endereço") e rodapé de 76px com Cancelar + Salvar.
 *
 * **Sem campos de crédito** (`FR-014`, AD-026): `LimiteCredito` e
 * `PermiteVendaCredito` existem no schema `ClienteCheckout`, mas
 * `PCheckout_PostCliente` não os grava — exibi-los faria o operador acreditar
 * que configurou algo que nunca foi persistido. Também não há campo de tipo de
 * pessoa: `CliTip` é hardcoded `'F'` dentro da procedure do ERP (AD-024), então
 * este caminho só cria pessoa física.
 *
 * Endereço é texto livre, sem validação de IBGE — só a máscara do CEP
 * (`FR-013`, AD-023).
 */
export interface FormCadastroSimplificadoProps {
  readonly aberto: boolean;
  /** Pré-preenche o CPF quando a busca que não achou nada era um documento. */
  readonly cpfInicial?: string;
  readonly onFechar: () => void;
  readonly onConfirmar: (dados: CadastroSimplificadoInput) => Promise<void> | void;
}

type CampoCadastro = keyof CadastroSimplificadoInput;

const CAMPOS_VAZIOS: CadastroSimplificadoInput = {
  nome: '',
  cpf: '',
  email: '',
  celular: '',
  cep: '',
  endereco: '',
  bairro: '',
  numero: '',
  cidade: '',
  uf: '',
};

export function FormCadastroSimplificado({
  aberto,
  cpfInicial = '',
  onFechar,
  onConfirmar,
}: FormCadastroSimplificadoProps): ReactElement | null {
  const [valores, setValores] = useState<CadastroSimplificadoInput>({
    ...CAMPOS_VAZIOS,
    cpf: cpfInicial,
  });
  const [enviando, setEnviando] = useState(false);

  // Mesmo padrão de `ModalBuscaProduto`: o componente não desmonta ao fechar,
  // então reabrir precisa começar do zero — ajustado durante a renderização,
  // não num efeito, para não haver um quadro com os dados do cadastro anterior.
  const [abertoAnterior, setAbertoAnterior] = useState(aberto);
  if (aberto !== abertoAnterior) {
    setAbertoAnterior(aberto);
    if (aberto) {
      setValores({ ...CAMPOS_VAZIOS, cpf: cpfInicial });
      setEnviando(false);
    }
  }

  const { montado, saindo } = usePresenca(aberto, DURACAO_SAIDA_MODAL_MS);
  const janelaRef = useFocoDeModal<HTMLFormElement>(aberto);

  // Fechar não desmonta na hora: o overlay fica no DOM pelo tempo da
  // animação de saída (`usePresenca`).
  if (!montado) {
    return null;
  }

  function alterar(campo: CampoCadastro, valor: string): void {
    setValores((atuais) => ({ ...atuais, [campo]: valor }));
  }

  // `FR-012`: formato de CPF e CEP validados **antes** do envio. Nome é o único
  // outro campo obrigatório — os demais o ERP aceita vazios.
  const cpfValido = validarFormatoCPF(valores.cpf);
  const cepValido = validarFormatoCEP(valores.cep);
  const podeEnviar = valores.nome.trim() !== '' && cpfValido && cepValido && !enviando;

  async function confirmar(): Promise<void> {
    if (!podeEnviar) {
      return;
    }
    setEnviando(true);
    try {
      await onConfirmar(valores);
    } finally {
      setEnviando(false);
    }
  }

  // Medidas do desenho a partir de `md:` (a árvore desktop, AD-198); no compacto
  // folgas, rótulos e campos encolhem para o formulário inteiro caber sem rolar
  // (correção do usuário, 2026-10-05: 563px de conteúdo para 436 de faixa em
  // 360×640). O texto digitado fica em 16px nos dois: abaixo disso o Safari dá
  // zoom ao focar o campo.
  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-start justify-center bg-[color-mix(in_srgb,var(--cc-color-ink)_40%,transparent)] p-sm md:p-lg',
        saindo ? 'cc-backdrop-sai' : 'cc-backdrop-entra',
      )}
      data-testid="modal-cadastro-cliente"
      onKeyDown={(evento) => {
        if (evento.key === 'Escape') {
          onFechar();
        }
      }}
    >
      <form
        ref={janelaRef}
        role="dialog"
        aria-modal="true"
        aria-label="Cadastrar cliente"
        className={cn(
          'flex max-h-full w-full max-w-[640px] flex-col overflow-hidden rounded-xl border border-border bg-background shadow-lg',
          saindo ? 'cc-modal-sai' : 'cc-modal-entra',
        )}
        onSubmit={(evento) => {
          evento.preventDefault();
          void confirmar();
        }}
      >
        <header className="flex h-16 shrink-0 items-center justify-between gap-sm border-b border-border px-base md:h-[78px] md:px-lg">
          <div className="flex items-center gap-sm">
            <span className="flex size-[42px] shrink-0 items-center justify-center rounded-full bg-secondary">
              <UserAdd className="size-5 text-primary" aria-hidden="true" />
            </span>
            <div className="flex flex-col gap-[2px]">
              <h2 className="text-xl font-semibold text-foreground">Cadastrar cliente</h2>
              <p className="text-sm font-medium text-muted-foreground">
                Preencha as informações do novo cliente
              </p>
            </div>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="icon-lg"
            className="shrink-0 rounded-full"
            aria-label="Fechar"
            onClick={onFechar}
          >
            <X className="size-4.5" aria-hidden="true" />
          </Button>
        </header>

        <div className="flex flex-1 flex-col gap-sm overflow-y-auto p-base md:gap-md md:p-md">
          <section className="flex flex-col gap-xs md:gap-base">
            <h3 className="text-md font-bold text-foreground">Dados do cliente</h3>
            <Campo
              campo="nome"
              rotulo="Nome completo"
              placeholder="Digite o nome completo"
              valor={valores.nome}
              onChange={alterar}
              autoFocus
            />
            <div className="flex gap-base">
              <Campo
                campo="cpf"
                rotulo="CPF"
                placeholder="000.000.000-00"
                valor={valores.cpf}
                onChange={alterar}
                invalido={valores.cpf !== '' && !cpfValido}
                dica="Informe os 11 dígitos do CPF."
                inputMode="numeric"
              />
              <Campo
                campo="email"
                rotulo="E-mail (opcional)"
                placeholder="nome@exemplo.com"
                valor={valores.email}
                onChange={alterar}
              />
              <Campo
                campo="celular"
                rotulo="Celular (opcional)"
                placeholder="(11) 90000-0000"
                valor={valores.celular}
                onChange={alterar}
                inputMode="tel"
              />
            </div>
          </section>

          <section className="flex flex-col gap-xs md:gap-base">
            <h3 className="text-md font-bold text-foreground">Endereço</h3>
            <div className="flex gap-base">
              <Campo
                campo="cep"
                rotulo="CEP"
                placeholder="00000-000"
                valor={valores.cep}
                onChange={alterar}
                // Máscara ao sair do campo, não a cada tecla (pedido do
                // usuário, 2026-09-03): formatar durante a digitação move o
                // cursor sozinho quando o operador volta para corrigir um
                // dígito. O que vai ao ERP são só os dígitos, de qualquer forma.
                aoSair={() => {
                  alterar('cep', formatarCEP(valores.cep));
                }}
                invalido={valores.cep !== '' && !cepValido}
                dica="Informe os 8 dígitos do CEP."
                largura="w-[140px]"
                inputMode="numeric"
              />
              <Campo
                campo="endereco"
                rotulo="Endereço"
                placeholder="Rua, avenida..."
                valor={valores.endereco}
                onChange={alterar}
              />
            </div>
            <div className="flex gap-base">
              <Campo
                campo="bairro"
                rotulo="Bairro"
                placeholder="Bairro"
                valor={valores.bairro}
                onChange={alterar}
              />
              <Campo
                campo="numero"
                rotulo="Número"
                placeholder="Nº"
                valor={valores.numero}
                onChange={alterar}
                largura="w-[140px]"
              />
            </div>
            <div className="flex gap-base">
              <Campo
                campo="cidade"
                rotulo="Cidade"
                placeholder="Cidade"
                valor={valores.cidade}
                onChange={alterar}
              />
              <Campo
                campo="uf"
                rotulo="UF"
                placeholder="UF"
                valor={valores.uf}
                onChange={alterar}
                largura="w-[100px]"
              />
            </div>
          </section>
        </div>

        <footer className="flex h-16 shrink-0 items-center justify-end gap-sm border-t border-border px-base md:h-[76px] md:px-lg">
          <Button
            type="submit"
            className="gap-xs rounded-full px-md py-sm text-lg font-semibold"
            data-testid="salvar-cliente"
            disabled={!podeEnviar}
          >
            <Check className="size-4.5" aria-hidden="true" />
            Salvar cliente
          </Button>
        </footer>
      </form>
    </div>
  );
}

interface CampoProps {
  readonly campo: CampoCadastro;
  readonly rotulo: string;
  readonly placeholder: string;
  readonly valor: string;
  readonly onChange: (campo: CampoCadastro, valor: string) => void;
  readonly invalido?: boolean;
  readonly dica?: string;
  /** Chamado ao perder o foco — hoje só o CEP usa, para aplicar a máscara. */
  readonly aoSair?: () => void;
  readonly largura?: string;
  readonly autoFocus?: boolean;
  readonly inputMode?: 'numeric' | 'tel';
}

/** Réplica do componente "Text Field" do Pencil (nó `d31Pi5`). */
function Campo({
  campo,
  rotulo,
  placeholder,
  valor,
  onChange,
  invalido = false,
  dica,
  aoSair,
  largura = 'flex-1',
  autoFocus = false,
  inputMode,
}: CampoProps): ReactElement {
  return (
    <label className={cn('flex min-w-0 flex-col gap-xxs md:gap-[7px]', largura)}>
      <span className="text-sm font-semibold text-foreground md:text-base">{rotulo}</span>
      <input
        className={cn(
          'h-10 rounded-lg border bg-background px-sm text-lg outline-none placeholder:text-[var(--cc-color-muted)] focus-visible:border-ring md:h-[46px] md:px-[14px]',
          invalido ? 'border-destructive' : 'border-border',
        )}
        data-testid={`campo-cadastro-${campo}`}
        autoComplete="off"
        autoFocus={autoFocus}
        {...(inputMode ? { inputMode } : {})}
        placeholder={placeholder}
        value={valor}
        aria-invalid={invalido}
        onChange={(evento) => {
          onChange(campo, evento.target.value);
        }}
        {...(aoSair ? { onBlur: aoSair } : {})}
      />
      {invalido && dica !== undefined ? (
        <span className="text-sm text-destructive">{dica}</span>
      ) : null}
    </label>
  );
}
